import React from 'react';
import cls from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSave,
    faCalendarTimes,
    faTimes,
    faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Row, Col, FormGroup, Spinner, Button } from 'reactstrap';
import Input from 'components/common/forms/Input';
import {
    FormLabel,
    FormValidated,
    SmallButton,
    onFieldChange,
    toasty,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import ValidationMessageDisplay from '../common/forms/ValidationMessageDisplay';
import { EmployeeCertification } from './EmployeeCertification';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { util } from '../Util';
import { handleFormSaveError } from '../common/forms/ValidationError';
import moment from 'moment';

export class EmployeeCertificationForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            errors: {},
            endDateError: '',
            employeeCertification:
                this.props.employeeCertification ?? new EmployeeCertification(),
            certifications: [],
            isValidated: false,
            attachmentFile: null,
            fileInputKey: Date.now(),

            showExpirationDate: false,
            showProofUpload: false,

            loading: true,
            saving: false, 
            isNew: !(this.props.employeeCertification?.id),
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onCertificationChanged = this.onCertificationChanged.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    showHideFieldsBasedOnCertification = (certificationId, certifications) => {
        const selectedCertification = certifications.find((c) => c.id === certificationId);

        if (!selectedCertification) {
            this.setState({
                showExpirationDate: false,
                showProofUpload: false,
            });
            return;
        }

        this.setState({
            showExpirationDate: !!selectedCertification.willExpire,
            showProofUpload: !!selectedCertification.proofRequired,
        });
    };

    onChange = onFieldChange;

    onClose(response) {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    }

    onCertificationChanged = (selection) => {
        const { employeeCertification } = this.state;
        employeeCertification.certificationId = selection?.id ?? null;
        this.setState({ employeeCertification }, () => {
            this.showHideFieldsBasedOnCertification(
                employeeCertification.certificationId,
                this.state.certifications,
            );
        });
    };

    onCertificationBlur = () => {
        const { employeeCertification, certifications } = this.state;
        this.showHideFieldsBasedOnCertification(
            employeeCertification.certificationId,
            certifications,
        );
    };

    onDelete = async () => {
        const { employeeCertification } = this.state;
        const url = ApiRoutes.employeeCertification.delete(employeeCertification.id);

        const fetchType = util.fetch.types.delete;

        const response = await util.fetch.andGetResponse(
            fetchType,
            url,
            employeeCertification,
            'Error deleting Certification',
            () => this.setState({ saving: false }),
        );

        if (response) {
            toasty.success(
                'Employee Certification deleted',
                'Employee Certification deleted successfully.',
            );
            this.onClose(response);
        }
    };

    handleDateChange = (e) => {
        const { name, value } = e.target;
        this.setState(
            (state) => ({
                employeeCertification: {
                    ...state.employeeCertification,
                    [name]: value,
                },
            }),
            this.validateDates,
        );
    };

    validateDates = () => {
        const { effectiveDate, expirationDate } = this.state.employeeCertification;
        let endDateError = '';

        if (
            moment(expirationDate).isValid() &&
            moment(effectiveDate).isValid() &&
            moment(expirationDate).isBefore(moment(effectiveDate))
        ) {
            endDateError = 'Expiration Date cannot be earlier than Effective Date.';
        }

        this.setState({ endDateError });
    };

    handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            this.setState((state) => ({
                employeeCertification: {
                    ...state.employeeCertification,
                    attachmentFile: file,
                },
            }));
        }
    };

    onSubmit = async (e) => {
        if (this.state.saving) return;

        const { employeeCertification, endDateError, isNew } = this.state;
        if (endDateError) return;

        if (!employeeCertification.expirationDate) {
            employeeCertification.expirationDate = null;
        }

        this.setState({ errors: {}, endDateError: '', saving: true });

        const formData = new FormData();
        formData.append('EmployeeId', employeeCertification.employeeId);
        formData.append('CertificationId', employeeCertification.certificationId);
        formData.append('EffectiveDate', employeeCertification.effectiveDate);
        formData.append('ExpirationDate', employeeCertification.expirationDate || '');
        formData.append('Id', employeeCertification.id);
        if (employeeCertification.attachmentFile) {
            formData.append('AttachmentFile', employeeCertification.attachmentFile);
        }

        const url = isNew
            ? ApiRoutes.employeeCertification.create()
            : ApiRoutes.employeeCertification.update(employeeCertification.id);

        const method = isNew ? 'POST' : 'PUT';

        try {
            const response = await fetch(url, { method, body: formData });

            if (!response.ok) {
                throw new Error('Error saving certification');
            }

            const result = await response.json();
            this.onClose(result);

            const description = isNew ? 'created' : 'updated';
            toasty.success(
                `Employee Certification ${description}`,
                `Employee Certification ${description} successfully.`,
            );

            this.props.onSaveCallback();
        } catch (error) {
            this.handleSaveError(error);
        } finally {
            this.setState({ saving: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    isEndDateRequired = () => {
        const { employeeCertification, certifications } = this.state;
        if (!employeeCertification?.certificationId) return false;

        const foundCertification = certifications.find(
            (c) => c.id === employeeCertification.certificationId,
        );

        return Boolean(foundCertification?.requiresEndDate);
    };

    openEmployee(employeeId) {
        const employeeCertification = new EmployeeCertification();
        employeeCertification.employeeId = employeeId;
        this.openEmployeeCertification(employeeCertification, true);
    }

    openEmployeeCertification(employeeCertification, isNew = false) {
        employeeCertification.effectiveDate = util.date.getInputFormat(
            employeeCertification.effectiveDate,
        );
        employeeCertification.expirationDate = util.date.getInputFormat(
            employeeCertification.expirationDate,
        );
        employeeCertification.attachmentFile = null;

        this.setState(
            {
                employeeCertification,
                isNew,
                fileInputKey: Date.now(),
            },
            () => {
                this.showHideFieldsBasedOnCertification(
                    employeeCertification.certificationId,
                    this.state.certifications,
                );
            },
        );

        this.props.toggleShow(true);
        this.context.setFormOpened(true);
    }

    populateState = async () => {
        try {
            const certifications = await util.fetch.js(ApiRoutes.certification.all());
            this.setState({ certifications, loading: false });
        } catch (error) {
            console.error('Error populating state:', error);
            this.setState({ loading: false });
        }
    };

    resetForm = () => {
        this.setState({
            formValidated: false,
            employeeCertification: new EmployeeCertification(),
            errors: {},
            endDateError: '',
            isValidated: false,
            validationMessage: null,
            saving: false,
        });
    };
    render() {
        if (this.state.loading) {
            return (
                <div className="d-flex flex-row h-100 align-items-center justify-content-center">
                    <Spinner />
                </div>
            );
        }

        const {
            errors,
            validationMessage,
            employeeCertification,
            certifications,
            isValidated,
            endDateError,
            showExpirationDate,
            showProofUpload,
            isNew,
            saving,
        } = this.state;
        const { show } = this.props;

        if (!employeeCertification) return null;

        const classNames = cls('slide-form col-xl-3 col-md-6 col-sm-12', { show });

        return (
            <div className={classNames}>
                <FormValidated
                    ref={this.formRef}
                    setIsValidated={(value) => this.setState({ isValidated: value })}
                    isValidated={isValidated}
                    className="m-0 w-100"
                    id="employeeCertificationForm"
                    name="employeeCertificationForm"
                    onSubmit={this.onSubmit}
                >
                    <input
                        type="hidden"
                        name="employeeCertification.employeeId"
                        value={employeeCertification.employeeId ?? ''}
                    />
                    <input
                        type="hidden"
                        name="employeeCertification.id"
                        value={employeeCertification.id ?? ''}
                    />

                    <header className="border-bottom d-flex flex-column position-relative pt-3 pl-3 pr-3 mb-3">
                        <Button
                            color="link"
                            id="employeeCertificationFormClose"
                            className="site-form-close"
                            onClick={this.onClose}
                        >
                            <FontAwesomeIcon size="lg" icon={faTimes} />
                        </Button>

                        <h5 className="mb-2 d-flex flex-row align-items-center">
                            <FontAwesomeIcon
                                className="mr-2 text-muted"
                                size="lg"
                                icon={faCalendarTimes}
                            />
                            <span>{isNew ? 'Add New Certification' : 'Edit Certification'}</span>
                        </h5>

                        <div className="d-flex flex-row align-items-center text-muted">
                            <SmallButton type="submit" disabled={saving}>
                                {saving ? (
                                    <Spinner size="sm" className="mr-2" />
                                ) : (
                                    <FontAwesomeIcon className="mr-2" icon={faSave} />
                                )}
                                Save
                            </SmallButton>

                            {employeeCertification.id > 0 && (
                                <SmallButton onClick={this.onDelete} disabled={saving}>
                                    <FontAwesomeIcon className="mr-2" icon={faTrashAlt} />
                                    Remove
                                </SmallButton>
                            )}
                        </div>
                    </header>

                    <ValidationMessageDisplay
                        onClear={this.onClearErrorNotificationClicked}
                        errors={errors}
                        message={validationMessage}
                    />


                    <Row className="pl-3 pr-3 pb-3 mr-0 ml-0">
                        <Col>
                            <FormGroup>
                                <FormLabel htmlFor="certificationsDropdown" text="Certification" required />
                                <ValidatedSelect
                                    className="w-auto"
                                    isClearable={false}
                                    id="certificationsDropdown"
                                    isDisabled={!isNew}
                                    name="employeeCertification.certificationId"
                                    required
                                    value={
                                        certifications.find((c) => c.id === employeeCertification.certificationId) ??
                                        ''
                                    }
                                    options={certifications}
                                    onChange={this.onCertificationChanged}
                                    onBlur={this.onCertificationBlur}
                                    getOptionLabel={(o) => o.name}
                                    getOptionValue={(o) => o.id}
                                    validationMessage="A certification is required."
                                />
                            </FormGroup>

                            <Input
                                label="Effective Date"
                                id="effectiveDate"
                                required
                                type="date"
                                className="form-control"
                                name="effectiveDate"
                                value={employeeCertification.effectiveDate || ''}
                                onChange={this.handleDateChange}
                                error="Effective Date is required."
                            />

                            {showExpirationDate && (
                                <Input
                                    type="date"
                                    id="expirationDate"
                                    name="expirationDate"
                                    label="Expiration Date"
                                    required={showExpirationDate}
                                    value={employeeCertification.expirationDate || ''}
                                    onChange={this.handleDateChange}
                                    error={endDateError}
                                />
                            )}

                            {showProofUpload && (
                                <FormGroup>
                                    <FormLabel htmlFor="attachmentFile" text="Attachment" required />

                                    <input
                                        id="attachmentFile"
                                        type="file"
                                        name="attachmentFile"
                                        key={this.state.fileInputKey}
                                        onChange={this.handleFileChange}
                                        required
                                    />

                                    {employeeCertification.normalizedFileName && (
                                        <div className="mb-2">
                                            <strong>Current File:</strong> {employeeCertification.normalizedFileName}
                                        </div>
                                    )}
                                </FormGroup>
                            )}
                        </Col>
                    </Row>
                </FormValidated>
            </div>
        );
    }
}
